import * as THREE from 'three';
import { ENV, GROUND } from '../core/SimConfig.js';
import { smoothstep } from '../utils.js';

/**
 * Per-wheel ground contact simulation.
 *
 * For each landing gear leg, casts a ray from the gear attachment point along
 * the strut axis. When a wheel penetrates the ground plane, applies:
 *   - Normal force (stiff spring + critical damping)
 *   - Longitudinal rolling friction
 *   - Lateral side friction (tires resist sliding sideways)
 *
 * Forces are applied at the wheel contact point via Aircraft.applyForceAtPoint(),
 * producing correct torques about the CG from the tricycle geometry.
 */

// Reusable temporaries
const _wheelWorld = new THREE.Vector3();
const _strutDir = new THREE.Vector3();
const _wheelVel = new THREE.Vector3();
const _omega = new THREE.Vector3();
const _r = new THREE.Vector3();
const _normalForce = new THREE.Vector3();
const _frictionForce = new THREE.Vector3();
const _totalWheelForce = new THREE.Vector3();
const _groundVel = new THREE.Vector3();
const _forward = new THREE.Vector3();
const _lateralDir = new THREE.Vector3();
const _localDown = new THREE.Vector3();
const _omegaCrossR = new THREE.Vector3();
const _angDampTorque = new THREE.Vector3();

export default class GroundContact {
  /**
   * @param {THREE.Object3D} modelRoot — the loaded GLB scene graph
   */
  constructor(modelRoot) {
    // Extract gear attachment points from the model's deployed pose.
    // We need the world-space position of each gear mesh origin when the gear
    // is fully deployed. Since the model stores gear in retracted pose and the
    // animator handles deployment, we read the mesh positions from the scene
    // graph and store them as body-local offsets.
    this.wheels = [];

    const gearMeshNames = [
      { name: 'nose',  mesh: '_Gear_Front' },
      { name: 'left',  mesh: '_Gear_Main_Left' },
      { name: 'right', mesh: '_Gear_Main_Right' },
    ];

    for (const def of gearMeshNames) {
      const obj = modelRoot.getObjectByName(def.mesh);
      if (!obj) {
        console.warn(`GroundContact: mesh "${def.mesh}" not found`);
        continue;
      }
      this.wheels.push({
        name: def.name,
        obj, // reference to the animated mesh — we read its world transform each frame
        // Persistent per-wheel debug-info record (pushed into wheelContacts each
        // frame). Reused rather than reallocated so the raycast loop doesn't
        // clone three Vector3s per wheel every frame.
        info: {
          name: def.name,
          pivot: new THREE.Vector3(),
          contact: new THREE.Vector3(),
          strutDir: new THREE.Vector3(),
          penetration: 0,
          normalForce: 0,
        },
      });
    }

    // State exposed for debug
    this.onGround = false;
    this.bellyContact = false; // true when fuselage is scraping the ground (gear retracted)
    this.wheelContacts = []; // { name, penetration, normalForce } per wheel this frame

    // Ground mode state
    this.blendToFlight = 1; // 0 = full ground mode, 1 = full flight mode
    this.restHeight = 0;    // CG height when sitting on wheels
    this.groundForce = new THREE.Vector3();  // simplified ground-mode force (world space)
    this.groundTorque = new THREE.Vector3(); // simplified ground-mode torque (world space)
    this.brakePitch = 0;    // current nose-down pitch from braking (rad, smoothed)
  }

  /**
   * Compute and apply ground contact forces for each wheel.
   * Must be called AFTER gear animation and BEFORE physics integration.
   *
   * @param {Aircraft} aircraft
   * @param {number}   dt — frame delta (seconds)
   * @param {import('./TerrainCollider.js').default|null} terrainCollider
   */
  update(aircraft, dt, terrainCollider = null) {
    this.wheelContacts.length = 0;
    this.onGround = false;
    this.bellyContact = false;
    for (const wheel of this.wheels) {
      const wg = aircraft.wheelGround[wheel.name];
      wg.onGround = false;
      wg.groundY = -Infinity; // "no ray cast this frame" — reads as no ground at all
    }

    // A destroyed wreck ploughs in and stops fast — scale up all friction.
    const fMul = aircraft.destroyed ? GROUND.destroyedFrictionMult : 1;

    const gearDeployed = aircraft.gear > 0.2;

    // ── Per-wheel raycasting (active whenever gear is not fully retracted) ──
    if (gearDeployed) for (const wheel of this.wheels) {
      // Get the gear mesh's current world transform (includes animation)
      wheel.obj.updateWorldMatrix(true, false);

      // Gear pivot point in world space
      _wheelWorld.setFromMatrixPosition(wheel.obj.matrixWorld);

      // Strut direction: the animator rotates gear struts ~-90° around local X
      // to deploy. Under a -90° X rotation, local -Z maps to world -Y (down).
      // So the deployed strut points along the mesh's local -Z transformed to
      // world space.
      _localDown.set(0, 0, -1);
      _strutDir.copy(_localDown).transformDirection(wheel.obj.matrixWorld);

      // Wheel contact point = pivot + strut direction * gear length
      _wheelWorld.addScaledVector(_strutDir, GROUND.gearLength);

      // Terrain height at wheel XZ (falls back to 0 if heightmap not loaded)
      const groundY = (terrainCollider
        ? (terrainCollider.getHeightAt(_wheelWorld.x, _wheelWorld.z) ?? 0)
        : 0) + GROUND.collisionYOffset;

      // Record ray data for debug visualization (updated with contact info
      // below). Reuse the wheel's persistent info record to avoid per-frame
      // Vector3 clones.
      const wheelInfo = wheel.info;
      wheelInfo.pivot.copy(_wheelWorld).addScaledVector(_strutDir, -GROUND.gearLength);
      wheelInfo.contact.copy(_wheelWorld);
      wheelInfo.strutDir.copy(_strutDir);
      wheelInfo.penetration = groundY - _wheelWorld.y;
      wheelInfo.normalForce = 0;
      this.wheelContacts.push(wheelInfo);

      // Check penetration
      const penetration = groundY - _wheelWorld.y; // positive when below ground

      // Publish this leg's ground state for the cosmetic wheel animation. The
      // planted flag uses a slack threshold (see GROUND.wheelContactTolerance)
      // so a wheel parked exactly on the rest-height boundary reads as planted
      // instead of chattering; the suspension works off groundY directly, so it
      // needs no threshold at all.
      const wg = aircraft.wheelGround[wheel.name];
      wg.groundY = groundY;
      wg.onGround = penetration > -GROUND.wheelContactTolerance;

      if (penetration <= 0) continue;

      this.onGround = true;

      // Velocity of wheel contact point = v_cg + omega × r
      _r.copy(_wheelWorld).sub(aircraft.position);
      _omega.copy(aircraft.angularVelocity);
      _wheelVel.copy(aircraft.velocity);
      _omegaCrossR.copy(_omega).cross(_r);
      _wheelVel.add(_omegaCrossR);

      // Vertical velocity at contact point (positive = moving up)
      const verticalVel = _wheelVel.y;

      // ── Normal force (spring + damper) ──────────────────────────────
      let Fn = GROUND.springK * penetration - GROUND.dampingC * verticalVel;
      Fn = Math.max(0, Fn); // normal force can only push, not pull
      Fn = Math.min(Fn, GROUND.maxNormalPerWheel); // cap to prevent explosion from deep penetration
      _normalForce.set(0, Fn, 0);

      // ── Friction forces ─────────────────────────────────────────────
      // Ground-plane velocity at contact point
      _groundVel.copy(_wheelVel);
      _groundVel.y = 0;

      const groundSpeed = _groundVel.length();
      _frictionForce.set(0, 0, 0);

      if (groundSpeed > 0.01) {
        // Wheel rolling direction = aircraft forward projected onto ground plane
        _forward.set(0, 0, -1).applyQuaternion(aircraft.quaternion);
        _forward.y = 0;
        const fwdLen = _forward.length();
        if (fwdLen > 0.001) _forward.divideScalar(fwdLen);

        // Decompose ground velocity into longitudinal (along rolling dir) and lateral
        const vLongitudinal = _groundVel.dot(_forward);
        _lateralDir.copy(_groundVel).addScaledVector(_forward, -vLongitudinal);
        const vLateral = _lateralDir.length();

        // Rolling / braking friction (opposes longitudinal motion)
        if (Math.abs(vLongitudinal) > 0.001) {
          const wb = aircraft.wheelBrake || 0;
          const mu = (wb > 0.1 ? GROUND.muRoll + (GROUND.muBrake - GROUND.muRoll) * wb : GROUND.muRoll) * fMul;
          const rollFriction = Math.min(mu * Fn, Math.abs(vLongitudinal) / dt * aircraft.mass / 3);
          _frictionForce.addScaledVector(_forward, -Math.sign(vLongitudinal) * rollFriction);
        }

        // Lateral friction (tires resist sideways sliding)
        if (vLateral > 0.001) {
          _lateralDir.divideScalar(vLateral); // normalize
          const sideFriction = Math.min(GROUND.muSide * fMul * Fn, vLateral / dt * aircraft.mass / 3);
          _frictionForce.addScaledVector(_lateralDir, -sideFriction);
        }
      }

      // ── Apply combined force at wheel contact point ─────────────────
      _totalWheelForce.copy(_normalForce).add(_frictionForce);
      aircraft.applyForceAtPoint(_totalWheelForce, _wheelWorld);

      wheelInfo.normalForce = Fn;
      wheelInfo.penetration = penetration;
    }

    // ── Belly contact (gear fully retracted) ─────────────────────────────
    if (!gearDeployed) {
      const bellyGroundY = (terrainCollider
        ? (terrainCollider.getHeightAt(aircraft.position.x, aircraft.position.z) ?? 0)
        : 0) + GROUND.collisionYOffset;
      const bellyPen = bellyGroundY + GROUND.bellyHeight - aircraft.position.y;
      if (bellyPen > 0) {
        this.onGround = true;
        this.bellyContact = true;

        // Normal force (spring + damper) applied at CG
        let Fn = GROUND.springK * bellyPen - GROUND.dampingC * aircraft.velocity.y;
        Fn = Math.max(0, Fn);
        Fn = Math.min(Fn, GROUND.maxNormalPerWheel);
        _normalForce.set(0, Fn, 0);

        // Friction — belly scraping has much higher friction than wheels
        _frictionForce.set(0, 0, 0);
        _groundVel.set(aircraft.velocity.x, 0, aircraft.velocity.z);
        const groundSpeed = _groundVel.length();

        if (groundSpeed > 0.01) {
          _forward.set(0, 0, -1).applyQuaternion(aircraft.quaternion);
          _forward.y = 0;
          const fwdLen = _forward.length();
          if (fwdLen > 0.001) _forward.divideScalar(fwdLen);

          const vLongitudinal = _groundVel.dot(_forward);
          _lateralDir.copy(_groundVel).addScaledVector(_forward, -vLongitudinal);
          const vLateral = _lateralDir.length();

          // Longitudinal belly friction (metal scraping on concrete)
          if (Math.abs(vLongitudinal) > 0.001) {
            const rollFriction = Math.min(
              GROUND.muBellyRoll * fMul * Fn,
              Math.abs(vLongitudinal) / dt * aircraft.mass,
            );
            _frictionForce.addScaledVector(_forward, -Math.sign(vLongitudinal) * rollFriction);
          }

          // Lateral belly friction
          if (vLateral > 0.001) {
            _lateralDir.divideScalar(vLateral);
            const sideFriction = Math.min(
              GROUND.muBellySide * fMul * Fn,
              vLateral / dt * aircraft.mass,
            );
            _frictionForce.addScaledVector(_lateralDir, -sideFriction);
          }
        }

        _totalWheelForce.copy(_normalForce).add(_frictionForce);
        aircraft.applyForceAtPoint(_totalWheelForce, aircraft.position);
      }
    }

    // ── Blend computation ────────────────────────────────────────────────
    const airspeed = aircraft.velocity.length();
    const t = (airspeed - GROUND.vGround) / (GROUND.vRotate - GROUND.vGround);
    this.blendToFlight = this.onGround ? smoothstep(t) : 1;

    // ── Ground mode forces and rest height ───────────────────────────────
    if (this.onGround && this.blendToFlight < 1) {
      this._computeRestHeight(aircraft, terrainCollider);
      this._computeGroundForces(aircraft, dt);
    } else {
      this.groundForce.set(0, 0, 0);
      this.groundTorque.set(0, 0, 0);
    }

    // Expose state to Aircraft (for update) and Controller (next frame)
    aircraft.onGround = this.onGround;
    aircraft.bellyContact = this.bellyContact;
    aircraft.blendToFlight = this.blendToFlight;
  }

  /**
   * Compute CG rest height so deepest wheel just touches ground.
   */
  _computeRestHeight(aircraft, terrainCollider = null) {
    const groundY = (terrainCollider
      ? (terrainCollider.getHeightAt(aircraft.position.x, aircraft.position.z) ?? 0)
      : 0) + GROUND.collisionYOffset;
    if (this.bellyContact) {
      this.restHeight = groundY + GROUND.bellyHeight;
      return;
    }
    let maxPen = 0;
    for (const wc of this.wheelContacts) {
      if (wc.penetration > maxPen) maxPen = wc.penetration;
    }
    this.restHeight = aircraft.position.y + maxPen;
  }

  /**
   * Simplified car-like forces for ground mode.
   * No gravity (Y is pinned kinematically), no lift, no aero torques.
   */
  _computeGroundForces(aircraft, dt) {
    this.groundForce.set(0, 0, 0);

    // Thrust projected onto ground plane
    _forward.set(0, 0, -1).applyQuaternion(aircraft.quaternion);
    _forward.y = 0;
    const fwdLen = _forward.length();
    if (fwdLen > 0.001) _forward.divideScalar(fwdLen);

    const thrustMag = aircraft.flightModel.maxThrust * (aircraft.thrustFactor || 0);
    this.groundForce.addScaledVector(_forward, thrustMag);

    // Ground speed (horizontal)
    _groundVel.set(aircraft.velocity.x, 0, aircraft.velocity.z);
    const groundSpeed = _groundVel.length();

    // Rolling friction (opposes ground-plane motion)
    if (groundSpeed > 0.01) {
      _groundVel.divideScalar(groundSpeed); // normalize to direction

      // A destroyed wreck ploughs in and stops fast — scale up all friction.
      const fMul = aircraft.destroyed ? GROUND.destroyedFrictionMult : 1;
      const weight = aircraft.mass * ENV.gravity;
      let mu;
      if (this.bellyContact) {
        mu = GROUND.muBellyRoll;
      } else {
        // Rolling friction only — a closed throttle is not a parking brake, so
        // the aircraft coasts down on tyre drag alone and needs the brakes to
        // stop promptly.
        mu = GROUND.muRoll;
        if (aircraft.wheelBrake > 0.1) mu = GROUND.muRoll + (GROUND.muBrake - GROUND.muRoll) * aircraft.wheelBrake;
      }
      mu *= fMul;
      this.groundForce.addScaledVector(_groundVel, -mu * weight);

      // Lateral friction — prevents sideways sliding (replaces kinematic constraint)
      const vLateral = aircraft.velocity.x * _forward.z - aircraft.velocity.z * _forward.x;
      if (Math.abs(vLateral) > 0.01) {
        // Lateral direction (perpendicular to forward in ground plane)
        _lateralDir.set(_forward.z, 0, -_forward.x);
        const muSide = (this.bellyContact ? GROUND.muBellySide : GROUND.muSide) * fMul;
        const maxSideFriction = muSide * weight;
        const neededFriction = Math.abs(vLateral) / dt * aircraft.mass;
        const sideFriction = Math.min(maxSideFriction, neededFriction);
        this.groundForce.addScaledVector(_lateralDir, -Math.sign(vLateral) * sideFriction);
      }
    }

    // Yaw steering torque (nosewheel + yaw damping)
    // Ramp up from rest, then taper off at higher speeds to prevent over-steering
    const steerRampUp = Math.min(groundSpeed / 5, 1);
    const speedFrac = Math.min(groundSpeed / GROUND.steeringMaxSpeed, 1);
    const steerLimit = 1 - speedFrac * (1 - GROUND.steeringMinFraction);
    const steeringTorque = aircraft.rudder * GROUND.steeringAuthority * steerRampUp * steerLimit;

    // Yaw resistance. The viscous term alone only ever asymptotes toward zero,
    // so an airframe braking to a halt out of a turn keeps pivoting on the spot
    // for seconds after it has stopped translating. Real tyres don't do that —
    // once the aircraft is stationary the contact patches grip and the rotation
    // simply stops. Add a rate-independent (Coulomb) scrub torque alongside it.
    const yawRate = aircraft.angularVelocity.y;
    let yawResist = -yawRate * GROUND.yawGroundDamping;

    // The scrub fades out with both ground speed and steering command: once
    // rolling, or while the pilot is actively steering, tyre scrub is already
    // accounted for inside steeringAuthority (which is the *net* of the nose
    // wheel's side force against the mains' resistance), so applying it again
    // here would just cancel the turn.
    const scrubFade = (1 - Math.min(groundSpeed / GROUND.yawScrubSpeed, 1))
      * (1 - Math.min(Math.abs(steeringTorque) / GROUND.steeringAuthority, 1));
    if (scrubFade > 0) {
      yawResist -= Math.sign(yawRate) * GROUND.yawScrubTorque * scrubFade;
    }

    // Cap the combined resistance at exactly the torque that nulls the current
    // yaw rate this step — both terms oppose the rotation, so together they
    // could otherwise overshoot into an oscillation.
    const stopTorque = Math.abs(yawRate) * aircraft.momentOfInertia.yaw / dt;
    if (Math.abs(yawResist) > stopTorque) yawResist = -Math.sign(yawRate) * stopTorque;

    this.groundTorque.set(0, steeringTorque + yawResist, 0);

    // Brake suspension pitch — nose dips proportional to braking deceleration
    const wb = aircraft.wheelBrake || 0;
    const brakePitchTarget = wb > 0.1
      ? -GROUND.brakePitchMax * wb * Math.min(groundSpeed / 10, 1) // scale with speed so it fades at rest
      : 0;
    this.brakePitch += (brakePitchTarget - this.brakePitch) * (1 - Math.exp(-GROUND.brakePitchRate * dt));
  }
}
